import { config } from "../components/constant";
import { commonFunction } from "../components/common-functions";

export const formUrlEncoded = x => Object.keys(x).reduce((p, c) => {
  if (x[c] === undefined || x[c] === null) {
    return p;
  }
  return `${p}${p ? '&' : ''}${c}=${encodeURI(x[c])}`;
}, '');

export const getAccessToken = () => {
  return localStorage.getItem(config.TOKEN_KEY);
}

export const downloadTemplate = (nanobar, elid, filename) => {
  const token = getAccessToken();
  nanobar.current.classList.add('running');
  nanobar.current.setAttribute('data-state', 'running');

  fetch(`${config.API_URL}api/v1/document/download/${elid}?access_token=${token}`)
    .then(resp => resp.blob())
    .then(blob => {
      if (
        nanobar.current && nanobar.current.classList.contains('running') &&
        nanobar.current.getAttribute('data-state') === 'running'
      ) {
        nanobar.current.setAttribute('data-state', 'paused');
      }

      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');

      a.style.display = 'none';
      a.href = url;
      a.download = filename;
      document.body.appendChild(a);
      a.click();

      window.URL.revokeObjectURL(url);
    })
    .catch(() => {
      commonFunction.showAlert("Unable to download the file!", 'error');
    });
};

export const downloadOutputFile = (nanobar, id, filename, openInNewTab = false, needBlob = false, callback) => {
  const token = getAccessToken();

  nanobar.current.classList.add("running");
  nanobar.current.setAttribute("data-state", "running");

  fetch(
    `${config.API_URL}api/v1/output/download/${id}?access_token=${token}`
  )
    .then((resp) => resp.blob())
    .then((blob) => {
      if (
        nanobar.current.classList.contains("running") &&
        nanobar.current.getAttribute("data-state") === "running"
      ) {
        nanobar.current.setAttribute("data-state", "paused");
      }

      if (needBlob && callback) {
        callback(blob);
        return;
      }

      const url = window.URL.createObjectURL(blob);
      if (openInNewTab) {
        const tab = window.open();
        tab.location.href = url;
        if (callback) {
          callback(true);
        }
        return;
      }

      const a = document.createElement("a");
      a.style.display = "none";
      a.href = url;
      a.download = filename;
      document.body.appendChild(a);
      a.click();

      window.URL.revokeObjectURL(url);
    })
    .catch(() => {
      commonFunction.showAlert("Unable to download the file!", 'error');
    });
};

export const googleDriveMimeTypesToNormalMimeTypeMapping = (mimeType) => {
  switch (mimeType) {
    case "application/vnd.google-apps.document":
      return {
        mimeType: "application/vnd.openxmlformats-officedocument.wordprocessingml.document",
        extension: ".docx"
      };
    case "application/vnd.google-apps.presentation":
      return {
        mimeType: "application/vnd.openxmlformats-officedocument.presentationml.presentation",
        extension: ".pptx"
      };
    case "application/vnd.google-apps.spreadsheet":
      return {
        mimeType: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
        extension: ".xlsx"
      };
    default:
      return { mimeType, extension: '' };
  }
};

export const processAIExtractedData = (data) => {
  if (!data || Array.isArray(data)) {
    return data;
  }

  const processedData = {};
  Object.keys(data).forEach(key => {
    const value = data[key];
    if (Array.isArray(value)) {
      if (key) {
        const cleanValue = value.reduce((acc, data) => {
          if (data && typeof data === 'object') {
            acc.push(data);
          }
          return acc;
        }, []);

        if (cleanValue.length > 0) {
          processedData[key.trim()] = cleanValue;
        }
      }
      return;
    }

    if (typeof value === 'object') {
      Object.keys(value).forEach(subKey => {
        if (key && subKey) {
          const keyToSet = `${key.trim()}_${subKey.trim()}`.replace(/\s+/g, '_');
          if (typeof value[subKey] === "object" && !Array.isArray(value[subKey])) {
            Object.keys(value[subKey]).forEach(subSubKey => {
              if (key && subKey) {
                const keyToSet = `${key.trim()}_${subKey.trim()}_${subSubKey.trim()}`.replace(/\s+/g, '_');
                processedData[keyToSet] = value[subKey][subSubKey];
              }
            });
            return;
          }
          processedData[keyToSet] = value[subKey];
        }
      });
      return;
    }

    if (value && key) {
      processedData[key.trim()] = value;
    }
  });

  return processedData;
};

/**
 * MongoDB/BSON forbids '.' in document field names; leading '$' is reserved.
 * Normalizes spaces to '_' so marker names stay stable for templates and queue storage.
 *
 * @param {unknown} segment
 * @returns {string}
 */
export const sanitizeAiMarkerKeySegment = (segment) => {
  if (segment == null || segment === "") return "";
  return String(segment)
    .trim()
    .replace(/\./g, "_")
    .replace(/^\$/, "_dollar_")
    .replace(/\s+/g, "_")
    .replace(/_+/g, "_");
};

/**
 * Same rules as keys produced by {@link combineAIExtractedSheetsToObject} (sheet + field).
 *
 * @param {unknown} sheetName
 * @param {unknown} fieldKey
 * @returns {string}
 */
export const buildCombinedSheetFieldKey = (sheetName, fieldKey) => {
  const a = sanitizeAiMarkerKeySegment(sheetName);
  const b = sanitizeAiMarkerKeySegment(fieldKey);
  return `${a}_${b}`;
};

/**
 * @param {Record<string, unknown>} obj
 * @returns {Record<string, unknown>}
 */
const sanitizePlainObjectKeys = (obj) => {
  if (!obj || typeof obj !== "object" || Array.isArray(obj)) return obj;
  const out = {};
  Object.keys(obj).forEach((k) => {
    const v = obj[k];
    const newKey = sanitizeAiMarkerKeySegment(k);
    out[newKey] =
      v && typeof v === "object" && !Array.isArray(v)
        ? sanitizePlainObjectKeys(v)
        : v;
  });
  return out;
};

/**
 * @param {unknown[]} rows
 * @returns {unknown[]}
 */
const sanitizeTableRowKeys = (rows) =>
  rows.map((row) =>
    row && typeof row === "object" && !Array.isArray(row)
      ? sanitizePlainObjectKeys(row)
      : row
  );

/**
 * Merge all sheet `data` maps from an AI extraction payload into one flat object.
 * Keys use {@link buildCombinedSheetFieldKey} so labels match AIExtractedDataReview and
 * values are safe to persist in MongoDB (no '.' in keys). Table rows are key-sanitized too.
 * Empty table arrays are omitted (tables are only shown in the UI when length > 0).
 *
 * @param {{ data?: Array<{ sheet_name?: string, data?: Record<string, unknown> }> }} extracted
 * @returns {Record<string, unknown>}
 */
export const combineAIExtractedSheetsToObject = (extracted) => {
  const out = {};
  const sheets = extracted?.data;
  if (!Array.isArray(sheets)) return out;

  sheets.forEach((sheet) => {
    const sheetName = sheet?.sheet_name ?? "";
    const sheetData = sheet?.data;
    if (!sheetData || typeof sheetData !== "object" || Array.isArray(sheetData)) return;

    Object.keys(sheetData).forEach((key) => {
      const value = sheetData[key];
      const combinedKey = buildCombinedSheetFieldKey(sheetName, key);
      if (Array.isArray(value)) {
        if (value.length > 0) out[combinedKey] = sanitizeTableRowKeys(value);
      } else {
        out[combinedKey] = value;
      }
    });
  });

  return out;
};
